import React, { useEffect, useMemo, useState } from "react";
import { ScopeSelect } from "./ScopeSelect";
import { useNavigate } from "react-router-dom";
import { GoogleMap, Marker, InfoWindow, MarkerClusterer } from "@react-google-maps/api";
import { GoogleMapError, useGMaps } from "../lib/gmaps";
import { useProjectLocations } from "../hooks/useProjectLocations";
import { projectNavigationUrl } from "../lib/projectLocation";
import { useApp } from "../context/AppContext";
import { TEAMS } from "../data/constants";
import ContactActions from "./ContactActions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";
import { MapPin, Filter, RefreshCw, Plus, ArrowRightLeft, Trash2, Loader2, Navigation, Phone, MessageSquare, ListPlus } from "lucide-react";

const REGION_LABEL = { la: { zh: "洛杉矶 LA", en: "Los Angeles" }, norcal: { zh: "北加州 NorCal", en: "NorCal" }, sd: { zh: "圣地亚哥 SD", en: "San Diego" } };
const openNav = (p) => {
  window.open(projectNavigationUrl(p), "_blank", "noopener");
};

const STATUS_META = {
  lead: { zh: "意向客户", en: "Lead", color: "#16a34a" },
  design: { zh: "设计阶段", en: "Design", color: "#2563eb" },
  construction: { zh: "施工阶段", en: "Construction", color: "#dc2626" },
  completed: { zh: "施工完成", en: "Completed", color: "#94a3b8" },
};
const ORDER = ["lead", "design", "construction", "completed"];
const BAY_CENTER = { lat: 37.44, lng: -121.98 };
const MAP_CONTAINER = { width: "100%", height: "100%" };

const teamLabel = (id, zh) => {
  const t = TEAMS.find((x) => x.id === id);
  return t ? (zh ? t.zh : t.name) : id || "-";
};

export default function ProjectMap() {
  const { api, lang, projects, refresh, user } = useApp();
  const zh = lang === "zh";
  const [rawPoints, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(() => new Set(ORDER));
  const [sales, setSales] = useState("all");
  const [team, setTeam] = useState("all");
  const [designer, setDesigner] = useState("all");
  const [region, setRegion] = useState(() => (["la", "norcal", "sd"].includes(user?.region) ? user.region : "all"));
  const nav = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [openInfo, setOpenInfo] = useState(null);
  const [map, setMap] = useState(null);
  const { isLoaded, loadError, errorCode } = useGMaps();
  const points = useProjectLocations(rawPoints, api, isLoaded);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/projects/map"); setPoints(data || []); }
    catch { setPoints([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const salesOpts = useMemo(() => Array.from(new Set(points.map((p) => p.sales).filter(Boolean))).sort(), [points]);
  const teamOpts = useMemo(() => Array.from(new Set(points.map((p) => p.team).filter(Boolean))).sort(), [points]);
  const designerOpts = useMemo(() => Array.from(new Set(points.map((p) => p.designer).filter(Boolean))).sort(), [points]);
  const regionOpts = useMemo(() => Array.from(new Set(points.map((p) => p.region).filter(Boolean))).sort(), [points]);

  const filtered = useMemo(() => points.filter((p) =>
    active.has(p.status) &&
    (sales === "all" || p.sales === sales) &&
    (team === "all" || p.team === team) &&
    (designer === "all" || p.designer === designer) &&
    (region === "all" || p.region === region)
  ), [points, active, sales, team, designer, region]);
  // 核对中的项目仍计入清单统计，但不能拿历史错误坐标画点。
  // 【2026-10-22】原来只判 `!= null`，而 useProjectLocations 对「已定位」地址用的是
  // `Number(result.lat)` —— 后端若回一个非数字（如 "N/A"），结果是 NaN，能通过 != null，
  // 于是 Marker 拿到 {lat: NaN}，MarkerClusterer 在 createClusters 里读 .lat 直接抛
  // TypeError，整个 CRM 看板被 CRA 错误遮罩盖住（实测复现）。
  // 这里统一按「可转成有限数字」判定，空串 / NaN / Infinity 一律不画点。
  const located = useMemo(() => filtered.filter((p) => {
    if (p.lat == null || p.lng == null || p.lat === "" || p.lng === "") return false;
    return Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng));
  }), [filtered]);

  // auto-focus: region-scoped view uses a stable metro center; "all" fits to points
  useEffect(() => {
    if (!map || !window.google) return;
    const RV = { la: { lat: 34.02, lng: -118.30, zoom: 10 }, norcal: { lat: 37.62, lng: -122.10, zoom: 10 }, sd: { lat: 32.85, lng: -117.15, zoom: 10 } };
    if (region !== "all" && RV[region]) {
      map.setCenter({ lat: RV[region].lat, lng: RV[region].lng });
      map.setZoom(RV[region].zoom);
      return;
    }
    if (located.length === 0) return;
    if (located.length === 1) { map.setCenter({ lat: located[0].lat, lng: located[0].lng }); map.setZoom(13); return; }
    const b = new window.google.maps.LatLngBounds();
    located.forEach((p) => b.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(b, 60);
  }, [map, region, located]);

  const counts = useMemo(() => {
    const c = {};
    ORDER.forEach((k) => { c[k] = points.filter((p) => p.status === k).length; });
    return c;
  }, [points]);

  const toggle = (k) => setActive((prev) => {
    const n = new Set(prev);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });

  const openProject = (id) => {
    const full = projects.find((x) => x.id === id);
    if (full) nav(`/project/${id}`);
    else toast.error(zh ? "未找到项目详情" : "Project not found");
  };

  const addFollowup = async (p) => {
    try {
      const title = zh ? `跟进客户 ${p.clientName || p.address || ""}`.trim() : `Follow up: ${p.clientName || p.address || ""}`;
      const due = new Date(); due.setHours(23, 59, 59, 0);
      await api.post("/todos", { title, description: p.address || "", priority: "high", dueDate: due.toISOString() });
      toast.success(zh ? "已加入今日待办跟进" : "Added to today's to-dos");
      setOpenInfo(null);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "添加失败" : "Failed")); }
  };

  const convertLead = async (lid) => {
    setBusyId(lid);
    try {
      await api.post(`/leads/${lid}/convert`, {});
      toast.success(zh ? "已转为设计阶段项目" : "Converted to a design-stage project");
      await load();
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "转换失败" : "Convert failed")); }
    setBusyId(null);
  };

  const deleteLead = async (lid) => {
    setBusyId(lid);
    try { await api.delete(`/leads/${lid}`); toast.success(zh ? "已删除意向客户" : "Lead deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "删除失败" : "Delete failed")); }
    setBusyId(null);
  };

  const selCls = "text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 focus:border-brand-400 focus:outline-none";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ceo-project-map">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-brand-500" />
            {zh ? "公司项目地图" : "Company Project Map"}
            <span className="text-xs font-normal text-slate-400">· {filtered.length}/{points.length}</span>
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{zh ? "按状态配色 · 点击项目点查看详情 · 可按销售/小组/设计师筛选" : "Click a project dot for details; filter by sales, team, designer"}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="map-add-lead" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors">
            <Plus className="h-3.5 w-3.5" />{zh ? "新增意向客户" : "Add Lead"}
          </button>
          <button data-testid="map-refresh" onClick={load} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-brand-300 hover:text-brand-500 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />{zh ? "刷新" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Status legend / toggles */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {ORDER.map((k) => {
          const m = STATUS_META[k];
          const on = active.has(k);
          return (
            <button key={k} data-testid={`map-status-${k}`} onClick={() => toggle(k)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${on ? "text-slate-800 border-slate-300 bg-slate-50" : "text-slate-300 border-slate-100 line-through"}`}>
              <i className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: on ? m.color : "#cbd5e1" }} />
              {zh ? m.zh : m.en} <span className="text-slate-400">({counts[k]})</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Filter className="h-3.5 w-3.5" /></span>
        <select data-testid="map-filter-sales" value={sales} onChange={(e) => setSales(e.target.value)} className={selCls}>
          <option value="all">{zh ? "全部销售" : "All sales"}</option>
          {salesOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <ScopeSelect kind="team" data-testid="map-filter-team" value={team} onChange={(e) => setTeam(e.target.value)} className={selCls}>
          <option value="all">{zh ? "全部小组" : "All teams"}</option>
          {teamOpts.map((tid) => <option key={tid} value={tid}>{teamLabel(tid, zh)}</option>)}
        </ScopeSelect>
        <select data-testid="map-filter-designer" value={designer} onChange={(e) => setDesigner(e.target.value)} className={selCls}>
          <option value="all">{zh ? "全部设计师" : "All designers"}{designerOpts.length === 0 ? (zh ? "（暂无）" : " (none)") : ""}</option>
          {designerOpts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <ScopeSelect kind="region" data-testid="map-filter-region" value={region} onChange={(e) => setRegion(e.target.value)} className={selCls}>
          <option value="all">{zh ? "全部区域" : "All regions"}</option>
          {regionOpts.map((r) => <option key={r} value={r}>{(REGION_LABEL[r] || { zh: r, en: r })[zh ? "zh" : "en"]}</option>)}
        </ScopeSelect>
      </div>

      {/* 与工程地图保持相同精度口径，公开地址范围后备不能描述为建筑精确坐标。 */}
      {filtered.some((p) => p.locationPrecision === "approximate") && <p className="mb-2 text-xs text-slate-500">
        {zh ? "部分位置按匹配门牌沿街估算，仅供地图参考；导航使用完整地址。" : "Some pins are estimated along the matched street address; navigation uses the full address."}
      </p>}
      {filtered.some((p) => p.locationStatus === "pending") && <p role="status" className="mb-2 text-sm text-slate-500">
        {zh ? "正在核对工地门牌位置…" : "Verifying site addresses…"}
      </p>}
      {filtered.some((p) => p.locationStatus === "unresolved") && <details className="mb-3 text-sm text-amber-700">
        <summary className="cursor-pointer py-2">{zh ? "部分地址定位待核对（点击查看）" : "Some addresses need verification"}</summary>
        <ul className="max-h-40 overflow-y-auto">
          {filtered.filter((p) => p.locationStatus === "unresolved").map((p) => <li key={p.id}>
            <button className="min-h-11 text-left underline" onClick={() => openNav(p)}>{p.address}</button>
          </li>)}
        </ul>
      </details>}
      <div className="rounded-xl overflow-hidden border border-slate-200" style={{ height: 480 }} data-testid="map-canvas">
        {loadError ? (
          <GoogleMapError zh={zh} errorCode={errorCode} testId="map-error" />
        ) : !isLoaded ? (
          <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400 text-sm"><Loader2 className="h-5 w-5 animate-spin mr-2" />{zh ? "地图加载中…" : "Loading map…"}</div>
        ) : (
          <GoogleMap mapContainerStyle={MAP_CONTAINER}
            onLoad={(m) => { m.setCenter(BAY_CENTER); m.setZoom(9); setMap(m); }} onUnmount={() => setMap(null)}
            options={{ mapTypeControl: true, streetViewControl: false, fullscreenControl: true, zoomControl: true,
              mapTypeId: "roadmap", clickableIcons: false, gestureHandling: "greedy",
              mapTypeControlOptions: window.google ? { style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR, mapTypeIds: ["roadmap", "hybrid"] } : undefined }}>
            <MarkerClusterer averageCenter gridSize={45} maxZoom={13}>
              {(clusterer) => (
                <>
                  {located.map((p) => {
                    const m = STATUS_META[p.status];
                    const isLead = p.status === "lead";
                    return (
                      <Marker key={p.id} position={{ lat: Number(p.lat), lng: Number(p.lng) }} title={`${p.clientName || p.address}${p.locationPrecision === "approximate" ? (zh ? " · 约略位置" : " · Approximate") : ""}`} clusterer={clusterer}
                        icon={window.google ? { path: window.google.maps.SymbolPath.CIRCLE, scale: isLead ? 8 : 7, fillColor: m.color, fillOpacity: 0.95, strokeColor: "#fff", strokeWeight: 1.5 } : undefined}
                        onClick={() => setOpenInfo(p.id)}>
                        {openInfo === p.id && (
                          <InfoWindow position={{ lat: Number(p.lat), lng: Number(p.lng) }} onCloseClick={() => setOpenInfo(null)}>
                            <>
                            {p.locationPrecision === "approximate" && <p className="mb-1 text-xs text-amber-700">{zh ? "约略位置 · 非建筑精确坐标" : "Approximate · not a rooftop location"}</p>}
                            {isLead ? (
                              <div className="text-xs space-y-1" style={{ minWidth: 180 }} data-testid={`lead-popup-${p.id}`}>
                                <p className="font-semibold text-slate-800">{p.address}</p>
                                <p><span style={{ color: m.color }} className="font-semibold">{zh ? "意向客户" : "Lead"}</span></p>
                                {p.clientName && <p><span className="text-slate-400">{zh ? "客户" : "Client"}: </span>{p.clientName}</p>}
                                {p.sales && <p><span className="text-slate-400">{zh ? "销售" : "Sales"}: </span>{p.sales}</p>}
                                {p.team && <p><span className="text-slate-400">{zh ? "小组" : "Team"}: </span>{teamLabel(p.team, zh)}</p>}
                                <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                                  <button data-testid={`lead-convert-${p.id}`} disabled={busyId === p.id} onClick={() => convertLead(p.id)}
                                    className="inline-flex items-center gap-1 text-[11px] text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md px-2 py-1">
                                    {busyId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}{zh ? "转为正式项目" : "Convert"}
                                  </button>
                                  <button data-testid={`lead-delete-${p.id}`} disabled={busyId === p.id} onClick={() => deleteLead(p.id)}
                                    className="inline-flex items-center gap-1 text-[11px] text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 rounded-md px-2 py-1">
                                    <Trash2 className="h-3 w-3" />{zh ? "删除" : "Delete"}
                                  </button>
                                  <ContactActions phone={p.phone} email={p.email} name={p.clientName} variant="map" idBase={`map-${p.id}`} />
                                  <button data-testid={`map-nav-${p.id}`} onClick={() => openNav(p)} title={zh ? "手机导航" : "Navigate"}
                                    className="inline-flex items-center gap-1 text-[11px] text-white bg-slate-700 hover:bg-slate-800 rounded-md px-2 py-1">
                                    <Navigation className="h-3 w-3" />{zh ? "导航" : "Navigate"}
                                  </button>
                                  <button data-testid={`map-followup-${p.id}`} onClick={() => addFollowup(p)} title={zh ? "加入今日待办跟进" : "Add follow-up"}
                                    className="inline-flex items-center gap-1 text-[11px] text-amber-700 border border-amber-300 hover:bg-amber-50 rounded-md px-2 py-1">
                                    <ListPlus className="h-3 w-3" />{zh ? "加入待办" : "Follow-up"}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs space-y-0.5" style={{ minWidth: 160 }}>
                                <p className="font-semibold text-slate-800">{p.address}</p>
                                <p><span className="text-slate-400">{zh ? "状态" : "Status"}: </span><span style={{ color: m.color }} className="font-semibold">{zh ? m.zh : m.en}</span></p>
                                {p.clientName && <p><span className="text-slate-400">{zh ? "客户" : "Client"}: </span>{p.clientName}</p>}
                                {p.sales && <p><span className="text-slate-400">{zh ? "销售" : "Sales"}: </span>{p.sales}</p>}
                                {p.phase && <p><span className="text-slate-400">{zh ? "阶段" : "Phase"}: </span>{p.phase}</p>}
                                <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                                  <button data-testid={`map-detail-${p.id}`} onClick={() => openProject(p.id)} className="inline-flex items-center gap-1 text-[11px] text-white bg-brand-500 hover:bg-brand-600 rounded-md px-2 py-1">{zh ? "详情 / 管理" : "Manage"}</button>
                                  <ContactActions phone={p.phone} email={p.email} name={p.clientName} variant="map" idBase={`map-${p.id}`} />
                                  <button data-testid={`map-nav-${p.id}`} onClick={() => openNav(p)} title={zh ? "手机导航" : "Navigate"} className="inline-flex items-center gap-1 text-[11px] text-white bg-slate-700 hover:bg-slate-800 rounded-md px-2 py-1"><Navigation className="h-3 w-3" />{zh ? "导航" : "Navigate"}</button>
                                  <button data-testid={`map-followup-${p.id}`} onClick={() => addFollowup(p)} title={zh ? "加入今日待办跟进" : "Add follow-up"} className="inline-flex items-center gap-1 text-[11px] text-amber-700 border border-amber-300 hover:bg-amber-50 rounded-md px-2 py-1"><ListPlus className="h-3 w-3" />{zh ? "加入待办" : "Follow-up"}</button>
                                </div>
                              </div>
                            )}
                            </>
                          </InfoWindow>
                        )}
                      </Marker>
                    );
                  })}
                </>
              )}
            </MarkerClusterer>
          </GoogleMap>
        )}
      </div>

      <CreateLeadDialog open={showCreate} onClose={() => setShowCreate(false)} api={api} zh={zh} salesOpts={salesOpts} onCreated={load} />
    </div>
  );
}

function CreateLeadDialog({ open, onClose, api, zh, salesOpts, onCreated }) {
  const [form, setForm] = useState({ address: "", clientName: "", sales: "", team: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.address.trim()) { toast.error(zh ? "请填写地址" : "Address required"); return; }
    setBusy(true);
    try {
      await api.post("/leads", form);
      toast.success(zh ? "已新增意向客户（绿色标记）" : "Lead added (green marker)");
      setForm({ address: "", clientName: "", sales: "", team: "" });
      onClose();
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "新增失败" : "Add failed")); }
    setBusy(false);
  };

  const inp = "w-full text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 focus:border-green-400 focus:outline-none";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="create-lead-dialog">
        <DialogHeader><DialogTitle>{zh ? "新增意向客户" : "Add Lead"}</DialogTitle>
          <DialogDescription>{zh ? "填写地址后自动定位，在地图上显示为绿色意向客户标记。" : "The address is geocoded and shown as a green lead marker."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">{zh ? "项目地址 *" : "Address *"}</label>
            <Input data-testid="lead-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder={zh ? "如：123 Main St, San Jose, CA 95112" : "e.g. 123 Main St, San Jose, CA 95112"} />
          </div>
          <div>
            <label className="text-xs text-slate-500">{zh ? "客户姓名" : "Client name"}</label>
            <Input data-testid="lead-client" value={form.clientName} onChange={(e) => set("clientName", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">{zh ? "销售" : "Sales"}</label>
              <input list="lead-sales-list" data-testid="lead-sales" value={form.sales} onChange={(e) => set("sales", e.target.value)} className={inp} />
              <datalist id="lead-sales-list">{salesOpts.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
            <div>
              <label className="text-xs text-slate-500">{zh ? "小组" : "Team"}</label>
              <ScopeSelect kind="team" data-testid="lead-team" value={form.team} onChange={(e) => set("team", e.target.value)} className={inp}>
                <option value="">{zh ? "未指定" : "Unassigned"}</option>
                {TEAMS.map((t) => <option key={t.id} value={t.id}>{zh ? t.zh : t.name}</option>)}
              </ScopeSelect>
            </div>
          </div>
          <p className="text-[11px] text-slate-400">{zh ? "保存后将自动定位并在地图上显示为绿色标记。" : "The lead will be geocoded and shown as a green marker."}</p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="lead-cancel">{zh ? "取消" : "Cancel"}</Button>
            <Button onClick={submit} disabled={busy} data-testid="lead-submit" className="bg-green-600 hover:bg-green-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (zh ? "保存" : "Save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
